import {
  BarChart as Container,
  barElementClasses,
} from '@mui/x-charts/BarChart';
import Typography from '@mui/material/Typography';
import { getRouterColor } from '@/utils/brandColors';
import { useTranslation } from 'react-i18next';
import { NoDataToDisplayBox } from './styles';

interface ChartProps {
  data: Record<string, string | number>[] | [];
  xAxisKey: string;
  seriesKey: string;
  layout?: 'horizontal' | 'vertical';
  height?: number;
  onItemClick?: (item: Record<string, string | number>) => void;
}

export default function BarChart({
  data,
  xAxisKey,
  seriesKey,
  layout = 'vertical',
  height = 300,
  onItemClick,
}: ChartProps) {
  const coloredData = data.map((item) => ({
    ...item,
    color: getRouterColor(String(item[xAxisKey])),
  }));

  const isHorizontal = layout === 'horizontal';
  const { t } = useTranslation();

  const categories = data.map((item) => String(item[xAxisKey]));
  const colors = categories.map((name) => getRouterColor(name));
  const colorMapProps = {
    colorMap: {
      type: 'ordinal' as const,
      values: categories,
      colors: colors,
    },
  };
  const truncateLabel = (label: string) =>
    label.length > 12 ? `${label.substring(0, 10)}...` : label;
  const maxValue = Math.max(...data.map((d) => Number(d[seriesKey]) || 0));
  const xAxisConfig = isHorizontal
    ? [
        {
          tickMinStep: 1,
          disableTicks: false,
          tickInterval:
            maxValue <= 5
              ? Array.from({ length: maxValue + 1 }, (_, i) => i)
              : undefined,
          valueFormatter: (v: number) => Math.round(v).toString(),
        },
      ]
    : [
        {
          scaleType: 'band' as const,
          dataKey: xAxisKey,
          ...colorMapProps,
          valueFormatter: truncateLabel,
          tickLabelStyle: { fontSize: 12, textAnchor: 'middle' as const },
        },
      ];
  const yAxisWidth = Math.min(
    100,
    Math.max(
      30,
      categories.reduce((max, label) => Math.max(max, label.length), 0) * 8 + 16
    )
  );

  const yAxisConfig = isHorizontal
    ? [
        {
          scaleType: 'band' as const,
          dataKey: xAxisKey,
          tickMaxStep: 1,
          width: yAxisWidth,
          ...colorMapProps,
          tickLabelStyle: {
            fontSize: 12,
            transform: 'translate(-5px, 0)',
          },
        },
      ]
    : undefined;

  if (!data || data.length === 0) {
    return (
      <NoDataToDisplayBox height={height}>
        <Typography color="text.secondary">
          {t('common.noDataToDisplay')}
        </Typography>
      </NoDataToDisplayBox>
    );
  }

  return (
    <Container
      dataset={coloredData}
      layout={layout}
      margin={{ top: 10, bottom: 30, left: 40 }}
      height={height}
      xAxis={xAxisConfig}
      yAxis={yAxisConfig}
      series={[
        {
          dataKey: seriesKey,
          valueFormatter: (value) =>
            `${value} ${value && value > 0 ? t('common.devices') : t('common.device')}`,
          highlightScope: { highlight: 'item', fade: 'global' },
        },
      ]}
      onItemClick={
        onItemClick
          ? (_event, item) => {
              const clickedItem = data[item.dataIndex];
              if (clickedItem) onItemClick(clickedItem);
            }
          : undefined
      }
      sx={{
        width: '100%',
        [`& .${barElementClasses.root}`]: {
          rx: 4,
          ry: 4,
          cursor: onItemClick ? 'pointer' : undefined,
        },
      }}
      grid={{ vertical: true, horizontal: true }}
    />
  );
}
